import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus, Mail, Lock, User, Loader2, Eye, EyeOff, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import { toast } from "sonner";

export default function RegisterPage() {
  const { registerPassword } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Mot de passe : 6 caractères minimum"); return; }
    setLoading(true);
    try {
      await registerPassword(email, password, name);
      toast.success("Bienvenue dans la famille A.F.B. !");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="register-page">
      <div className="bg-[#0A1024] text-white p-8 md:p-14 lg:p-16 flex flex-col">
        <Link to="/" className="text-sm text-white/70 hover:text-white inline-flex items-center gap-2 transition-colors">
          <ArrowLeft size={14} /> Retour au site
        </Link>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <h1 className="font-display text-4xl md:text-5xl font-bold">Devenir Membre</h1>
            <p className="text-white/65 text-sm mt-2">Créez votre compte A.F.B. — Section Diaspora</p>

            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="register-form">
              <div>
                <label className="text-[12px] font-medium text-white/85 uppercase tracking-wider">Nom complet</label>
                <div className="relative mt-2">
                  <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/45" />
                  <input type="text" required minLength={2} value={name} onChange={(e) => setName(e.target.value)}
                    data-testid="register-name-input"
                    className="w-full pl-10 pr-4 py-3.5 rounded-md bg-white/[0.06] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#0052CC] focus:bg-white/[0.09] transition-all"
                    placeholder="Jean-Marie Kabila" />
                </div>
              </div>
              <div>
                <label className="text-[12px] font-medium text-white/85 uppercase tracking-wider">Adresse email</label>
                <div className="relative mt-2">
                  <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/45" />
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="register-email-input"
                    className="w-full pl-10 pr-4 py-3.5 rounded-md bg-white/[0.06] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#0052CC] focus:bg-white/[0.09] transition-all"
                    placeholder="vous@email.com" />
                </div>
              </div>
              <div>
                <label className="text-[12px] font-medium text-white/85 uppercase tracking-wider">Mot de passe</label>
                <div className="relative mt-2">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/45" />
                  <input type={showPwd ? "text" : "password"} required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid="register-password-input"
                    className="w-full pl-10 pr-11 py-3.5 rounded-md bg-white/[0.06] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#0052CC] focus:bg-white/[0.09] transition-all"
                    placeholder="6 caractères minimum" />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} aria-label="Afficher" className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/45 hover:text-white/80">
                    {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full bg-[#3D5DD4] hover:bg-[#2C49B9] text-white rounded-md py-6 font-semibold text-sm tracking-wide btn-lift" data-testid="register-submit-btn">
                {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <UserPlus size={15} className="mr-2" />}
                Créer mon compte <ArrowRight size={15} className="ml-2" />
              </Button>

              <div className="flex items-center gap-3 my-2">
                <span className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">ou</span>
                <span className="flex-1 h-px bg-white/10" />
              </div>

              <Button type="button" onClick={handleGoogle} variant="outline" className="w-full bg-transparent border-white/15 text-white hover:bg-white hover:text-[#0A1024] rounded-md py-6 font-semibold text-sm tracking-wide transition-all" data-testid="google-register-btn">
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                S'inscrire avec Google
              </Button>
            </form>

            <p className="text-center text-sm text-white/65 mt-7">
              Déjà membre ? <Link to="/login" className="text-[#D4AF37] font-semibold hover:underline" data-testid="goto-login-link">Se connecter</Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex relative bg-gradient-to-br from-[#0052CC] via-[#003D99] to-[#0A1F44] text-white p-12 flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0 stars-pattern opacity-15" />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

        <div className="relative text-center max-w-md">
          <div className="flex justify-center mb-10">
            <div className="relative">
              <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
                <div className="w-72 h-72 rounded-full bg-[#D4AF37]/20 blur-3xl" />
              </div>
              <Logo variant="badge" size={240} />
            </div>
          </div>

          <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
            LES AMIS DE FATSHI BÉTON
          </h2>
          <div className="font-display font-bold text-[#D4AF37] mt-3 text-lg" style={{ letterSpacing: "0.18em" }}>
            SECTION DIASPORA
          </div>

          <div className="mt-8 mx-auto w-32 h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

          <p className="italic text-white/85 mt-7 text-base">
            « Pour un Congo fort, uni et prospère »
          </p>
        </div>
      </div>
    </div>
  );
}
